const {
    FRUIT_SYMBOL_VARIANT_ID,
    getUFOVSymbolVariant,
    normalizeSymbolVariantId,
} = require('./ufovSymbolSets');
const {
    getUFOVLiveScenarioCatalog,
    getUFOVScenarioRuntimeContext,
    normalizeScenarioId,
} = require('./ufovScenarioCatalog');

const MODE_IDS = {
    CLASSIC: 'classic',
    ADVANCED: 'advanced',
};

const POSITIONS = [
    { id: 'north-west', label: 'NW', angle: 225, gridArea: 'nw' },
    { id: 'north', label: 'N', angle: 270, gridArea: 'n' },
    { id: 'north-east', label: 'NE', angle: 315, gridArea: 'ne' },
    { id: 'west', label: 'W', angle: 180, gridArea: 'w' },
    { id: 'east', label: 'E', angle: 0, gridArea: 'e' },
    { id: 'south-west', label: 'SW', angle: 135, gridArea: 'sw' },
    { id: 'south', label: 'S', angle: 90, gridArea: 's' },
    { id: 'south-east', label: 'SE', angle: 45, gridArea: 'se' },
];

const VIDEO_SCENE_IDS = [
    'countdown',
    'pre-stimulus-blank',
    'stimulus',
    'post-stimulus-blank',
    'center-question',
    'direction-question',
    'result',
];
const LEVEL_MIN = 0;
const LEVEL_MAX = 100;
const HIGH_LEVEL_START = 20;
const PRESTIGE_LEVEL_START = 70;
const HIGH_LEVEL_START_EXPOSURE_MS = 149;
const PRESTIGE_LEVEL_START_EXPOSURE_MS = 20;
const LEVEL_MAX_EXPOSURE_MS = 12;
const MAX_ANSWER_OPTION_COUNT = 20;
const VIDEO_OPTION_LIMIT = MAX_ANSWER_OPTION_COUNT;

const VIDEO_BUCKETS = [
    { label: 'Easy', minLevel: 0, maxLevel: 19, minAge: 70, maxAge: 55 },
    { label: 'Medium', minLevel: 20, maxLevel: 39, minAge: 54, maxAge: 35 },
    { label: 'Hard', minLevel: 40, maxLevel: 70, minAge: 34, maxAge: 18 },
    { label: 'Expert', minLevel: 71, maxLevel: 100, minAge: 18, maxAge: 18 },
];

const VIDEO_THEMES = [
    {
        id: 'ultraviolet-pulse',
        pageBackground: 'radial-gradient(circle at 18% 16%, rgba(255, 255, 255, 0.16), transparent 32%), linear-gradient(160deg, #8b5cf6 0%, #6d28d9 45%, #312e81 100%)',
        textColor: '#f8fafc',
        mutedTextColor: 'rgba(248, 250, 252, 0.82)',
        accent: '#f472b6',
        accentSoft: 'rgba(244, 114, 182, 0.2)',
    },
    {
        id: 'red-alert',
        pageBackground: 'radial-gradient(circle at 22% 18%, rgba(255, 255, 255, 0.15), transparent 28%), linear-gradient(160deg, #ef4444 0%, #dc2626 42%, #7f1d1d 100%)',
        textColor: '#fff7f7',
        mutedTextColor: 'rgba(255, 247, 247, 0.82)',
        accent: '#fb7185',
        accentSoft: 'rgba(251, 113, 133, 0.2)',
    },
    {
        id: 'cobalt-rush',
        pageBackground: 'radial-gradient(circle at 84% 14%, rgba(255, 255, 255, 0.18), transparent 30%), linear-gradient(160deg, #2563eb 0%, #1d4ed8 44%, #172554 100%)',
        textColor: '#f8fbff',
        mutedTextColor: 'rgba(248, 251, 255, 0.82)',
        accent: '#60a5fa',
        accentSoft: 'rgba(96, 165, 250, 0.2)',
    },
    {
        id: 'hot-magenta',
        pageBackground: 'radial-gradient(circle at 18% 12%, rgba(255, 255, 255, 0.17), transparent 28%), linear-gradient(160deg, #ec4899 0%, #db2777 44%, #831843 100%)',
        textColor: '#fff8fc',
        mutedTextColor: 'rgba(255, 248, 252, 0.82)',
        accent: '#f9a8d4',
        accentSoft: 'rgba(249, 168, 212, 0.2)',
    },
    {
        id: 'emerald-night',
        pageBackground: 'radial-gradient(circle at 78% 18%, rgba(255, 255, 255, 0.14), transparent 28%), linear-gradient(160deg, #10b981 0%, #047857 42%, #052e2b 100%)',
        textColor: '#f4fffb',
        mutedTextColor: 'rgba(244, 255, 251, 0.82)',
        accent: '#34d399',
        accentSoft: 'rgba(52, 211, 153, 0.2)',
    },
    {
        id: 'midnight-flare',
        pageBackground: 'radial-gradient(circle at 24% 12%, rgba(255, 255, 255, 0.18), transparent 30%), linear-gradient(160deg, #0f172a 0%, #1e293b 52%, #020617 100%)',
        textColor: '#f8fafc',
        mutedTextColor: 'rgba(248, 250, 252, 0.82)',
        accent: '#f59e0b',
        accentSoft: 'rgba(245, 158, 11, 0.2)',
    },
];

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
const readNumber = (value, fallback) => {
    const numericValue = Number(value);
    return Number.isFinite(numericValue) ? numericValue : fallback;
};

const normalizeSeed = (seed) => `${seed ?? 'ufov-seed'}`;

const createSeed = () => `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

const xmur3 = (input) => {
    let hash = 1779033703 ^ input.length;

    for (let index = 0; index < input.length; index += 1) {
        hash = Math.imul(hash ^ input.charCodeAt(index), 3432918353);
        hash = (hash << 13) | (hash >>> 19);
    }

    return () => {
        hash = Math.imul(hash ^ (hash >>> 16), 2246822507);
        hash = Math.imul(hash ^ (hash >>> 13), 3266489909);
        hash ^= hash >>> 16;
        return hash >>> 0;
    };
};

const mulberry32 = (seed) => {
    let value = seed >>> 0;

    return () => {
        value += 0x6d2b79f5;
        let next = Math.imul(value ^ (value >>> 15), value | 1);
        next ^= next + Math.imul(next ^ (next >>> 7), next | 61);
        return ((next ^ (next >>> 14)) >>> 0) / 4294967296;
    };
};

const createSeededRng = (seed) => {
    const seedFactory = xmur3(normalizeSeed(seed));
    return mulberry32(seedFactory());
};

const normalizeRng = (rngInput) => {
    if (typeof rngInput === 'function') {
        return rngInput;
    }

    if (rngInput !== undefined && rngInput !== null) {
        return createSeededRng(rngInput);
    }

    return Math.random;
};

const randomInt = (rng, min, max) => min + Math.floor(rng() * (max - min + 1));

const pick = (items, rng) => items[Math.floor(rng() * items.length)];

const shuffle = (items, rngInput) => {
    const rng = normalizeRng(rngInput);
    const copy = [...items];

    for (let index = copy.length - 1; index > 0; index -= 1) {
        const swapIndex = Math.floor(rng() * (index + 1));
        [copy[index], copy[swapIndex]] = [copy[swapIndex], copy[index]];
    }

    return copy;
};

const getEarlyLevelExposureBonus = (level) => {
    if (level <= 0) {
        return 140;
    }

    if (level === 1) {
        return 90;
    }

    if (level === 2) {
        return 40;
    }

    return 0;
};

const getExposureSpeedCut = (level) => (
    Math.max(0, level - 14) * 3
    + Math.max(0, level - 32) * 2
);

const getExposureFloor = (level) => Math.round(
    clamp(
        140 - (Math.max(0, level - 18) * 2.5),
        70,
        140
    )
);

const getHighLevelExposureMs = (level) => {
    if (level < HIGH_LEVEL_START) {
        return null;
    }

    if (level <= PRESTIGE_LEVEL_START) {
        const ratio = clamp(
            (level - HIGH_LEVEL_START) / (PRESTIGE_LEVEL_START - HIGH_LEVEL_START),
            0,
            1
        );

        return Math.round(
            PRESTIGE_LEVEL_START_EXPOSURE_MS
            + ((HIGH_LEVEL_START_EXPOSURE_MS - PRESTIGE_LEVEL_START_EXPOSURE_MS) * ((1 - ratio) ** 2.1))
        );
    }

    const prestigeRatio = clamp(
        (level - PRESTIGE_LEVEL_START) / (LEVEL_MAX - PRESTIGE_LEVEL_START),
        0,
        1
    );
    const logDrop = Math.log1p(prestigeRatio * 9) / Math.log1p(9);

    return Math.round(
        LEVEL_MAX_EXPOSURE_MS
        + ((PRESTIGE_LEVEL_START_EXPOSURE_MS - LEVEL_MAX_EXPOSURE_MS) * (1 - logDrop))
    );
};

const getDifficulty = (level, modeId = MODE_IDS.CLASSIC) => {
    const safeLevel = clamp(Number(level) || 0, LEVEL_MIN, LEVEL_MAX);
    const exposureBonusMs = modeId === MODE_IDS.ADVANCED ? 180 : 0;
    const baseExposureMs = Math.round(
        1100 * (0.91 ** safeLevel)
        + exposureBonusMs
        + getEarlyLevelExposureBonus(safeLevel)
        - getExposureSpeedCut(safeLevel)
    );
    const highLevelExposureMs = getHighLevelExposureMs(safeLevel);
    const exposureMs = highLevelExposureMs ?? clamp(
        baseExposureMs,
        getExposureFloor(safeLevel),
        1280
    );

    return {
        level: safeLevel,
        optionCount: clamp(8 + Math.floor(safeLevel / 2) * 2, 8, MAX_ANSWER_OPTION_COUNT),
        distractorCount: clamp(22 + safeLevel * 4, 22, 128),
        exposureMs: clamp(exposureMs, LEVEL_MAX_EXPOSURE_MS, 1280),
        targetRadius: clamp(34 + Math.floor(safeLevel / 6), 34, 44),
        sameCategoryRatio: clamp(0.32 + safeLevel * 0.009, 0.32, 0.72),
        foilCategoryRatio: clamp(0.18 + safeLevel * 0.004, 0.18, 0.32),
        noiseOpacity: clamp(0.44 + safeLevel * 0.0048, 0.44, 0.72),
        motionPx: clamp(0.35 + safeLevel * 0.045, 0.35, 3.1),
        motionDeg: clamp(1.2 + safeLevel * 0.09, 1.2, 6.2),
        motionDurationMs: clamp(2400 - safeLevel * 18, 1200, 2400),
        fixationMs: 3000,
        maskMs: 560,
    };
};

const normalizeTrialStringList = (items) => {
    if (!Array.isArray(items)) {
        return [];
    }

    return [...new Set(items.map((item) => `${item || ''}`.trim()).filter(Boolean))];
};

const normalizeTrialOptions = (optionsOrRng) => {
    if (
        optionsOrRng === undefined
        || optionsOrRng === null
        || typeof optionsOrRng === 'function'
        || typeof optionsOrRng === 'number'
        || typeof optionsOrRng === 'string'
    ) {
        return {
            rngInput: optionsOrRng,
            symbolVariant: FRUIT_SYMBOL_VARIANT_ID,
        };
    }

    return {
        rngInput: optionsOrRng.rng ?? optionsOrRng.seed ?? null,
        optionLimit: optionsOrRng.optionLimit ? Number(optionsOrRng.optionLimit) : null,
        symbolVariant: normalizeSymbolVariantId(optionsOrRng.symbolVariant || optionsOrRng.variant || FRUIT_SYMBOL_VARIANT_ID),
        scenarioId: optionsOrRng.scenarioId ? `${optionsOrRng.scenarioId}`.trim().toLowerCase() : '',
        allowedSymbolIds: normalizeTrialStringList(optionsOrRng.allowedSymbolIds || optionsOrRng.symbolIds),
    };
};

const buildFilteredSymbolVariant = (symbolVariant, allowedSymbolIds = []) => {
    const allowed = new Set(normalizeTrialStringList(allowedSymbolIds));

    if (!allowed.size) {
        return symbolVariant;
    }

    const symbols = (symbolVariant.symbols || []).filter((symbol) => allowed.has(symbol.id));

    if (!symbols.length) {
        return symbolVariant;
    }

    const categories = [];
    const categoryMap = new Map();
    const categoryBySymbol = new Map();

    symbols.forEach((symbol) => {
        const categoryId = symbol.categoryId
            || symbolVariant.categoryBySymbol?.get(symbol.id)
            || `${symbolVariant.id}-selected`;

        if (!categoryMap.has(categoryId)) {
            categoryMap.set(categoryId, []);
            categories.push({
                id: categoryId,
                symbols: categoryMap.get(categoryId),
            });
        }

        categoryMap.get(categoryId).push(symbol.id);
        categoryBySymbol.set(symbol.id, categoryId);
    });

    return {
        ...symbolVariant,
        symbols,
        symbolMap: new Map(symbols.map((symbol) => [symbol.id, symbol])),
        pool: symbols.map((symbol) => symbol.id),
        categories: categories.map((category) => ({
            ...category,
            symbols: [...category.symbols],
        })),
        categoryMap: new Map([...categoryMap.entries()].map(([id, symbolIds]) => [id, [...symbolIds]])),
        categoryBySymbol,
        fallbackSymbolId: symbols.some((symbol) => symbol.id === symbolVariant.fallbackSymbolId)
            ? symbolVariant.fallbackSymbolId
            : symbols[0].id,
        maxOptionCount: Math.min(
            Number(symbolVariant.maxOptionCount) || symbols.length,
            symbols.length
        ),
    };
};

const getCategorySymbols = (symbolVariant, categoryId) => (
    symbolVariant.categoryMap.get(categoryId) || symbolVariant.pool
);

const pickCategory = (symbolVariant, rngInput, ...excludedCategoryIds) => {
    const rng = normalizeRng(rngInput);
    const availableCategories = symbolVariant.categories.filter(
        (category) => !excludedCategoryIds.includes(category.id)
    );

    return pick(availableCategories.length ? availableCategories : symbolVariant.categories, rng);
};

const takeDistinct = (items, count, blocked, rngInput) => {
    const picked = [];

    for (const item of shuffle(items, rngInput)) {
        if (picked.length >= count) {
            break;
        }

        if (!blocked.has(item)) {
            picked.push(item);
            blocked.add(item);
        }
    }

    return picked;
};

const finalizeAnswerOptions = (options, correctSymbolId, optionCount, symbolVariant, rngInput) => {
    const rng = normalizeRng(rngInput);
    const seen = new Set();
    const uniqueOptions = [];

    for (const option of options) {
        if (!option || seen.has(option)) {
            continue;
        }

        seen.add(option);
        uniqueOptions.push(option);
    }

    if (!seen.has(correctSymbolId)) {
        seen.add(correctSymbolId);
        uniqueOptions.unshift(correctSymbolId);
    }

    if (uniqueOptions.length > optionCount) {
        const trimmedOptions = uniqueOptions.filter((option) => option !== correctSymbolId);
        uniqueOptions.length = 0;
        uniqueOptions.push(correctSymbolId, ...trimmedOptions.slice(0, optionCount - 1));
        seen.clear();
        uniqueOptions.forEach((option) => seen.add(option));
    }

    if (uniqueOptions.length < optionCount) {
        uniqueOptions.push(
            ...takeDistinct(symbolVariant.pool, optionCount - uniqueOptions.length, seen, rng)
        );
    }

    return shuffle(uniqueOptions.slice(0, optionCount), rng);
};

const pickSymbolFromCategory = (symbolVariant, categoryId, excluded, rngInput) => {
    const rng = normalizeRng(rngInput);
    const blocked = new Set((excluded || []).filter(Boolean));
    const options = getCategorySymbols(symbolVariant, categoryId).filter((symbolId) => !blocked.has(symbolId));
    const fallback = symbolVariant.pool.filter((symbolId) => !blocked.has(symbolId));

    return pick(options.length ? options : (fallback.length ? fallback : symbolVariant.pool), rng);
};

const pickSymbolOutsideCategory = (symbolVariant, excludedCategoryId, excluded, rngInput) => {
    const rng = normalizeRng(rngInput);
    const blocked = new Set((excluded || []).filter(Boolean));
    const outsideCategory = symbolVariant.pool.filter((symbolId) => (
        !blocked.has(symbolId)
        && symbolVariant.categoryBySymbol.get(symbolId) !== excludedCategoryId
    ));
    const fallback = symbolVariant.pool.filter((symbolId) => !blocked.has(symbolId));

    return pick(outsideCategory.length ? outsideCategory : (fallback.length ? fallback : symbolVariant.pool), rng);
};

const createAnswerOptions = (correctSymbolId, difficulty, symbolVariant, rngInput) => {
    const rng = normalizeRng(rngInput);
    const optionCount = Math.min(
        difficulty.optionCount,
        symbolVariant.maxOptionCount || symbolVariant.pool.length
    );
    const correctCategoryId = symbolVariant.categoryBySymbol.get(correctSymbolId) || pickCategory(symbolVariant, rng).id;
    const foilCategoryId = pickCategory(symbolVariant, rng, correctCategoryId).id;
    const blocked = new Set([correctSymbolId]);
    const availableSame = getCategorySymbols(symbolVariant, correctCategoryId)
        .filter((symbolId) => symbolId !== correctSymbolId)
        .length;
    const requestedSame = Math.ceil(optionCount * difficulty.sameCategoryRatio);
    const maxSameCategoryCount = Math.max(0, Math.min(availableSame, optionCount - 3));
    const sameCategoryCount = maxSameCategoryCount ? clamp(requestedSame, 1, maxSameCategoryCount) : 0;
    const remainingAfterSame = optionCount - 1 - sameCategoryCount;
    const requestedFoil = Math.ceil(optionCount * difficulty.foilCategoryRatio);
    const foilCategoryCount = remainingAfterSame ? clamp(requestedFoil, 1, remainingAfterSame) : 0;
    const sameCategoryOptions = takeDistinct(getCategorySymbols(symbolVariant, correctCategoryId), sameCategoryCount, blocked, rng);
    const foilCategoryOptions = takeDistinct(getCategorySymbols(symbolVariant, foilCategoryId), foilCategoryCount, blocked, rng);
    const remainingCount = optionCount - 1 - sameCategoryOptions.length - foilCategoryOptions.length;
    const mixedOptions = takeDistinct(symbolVariant.pool, remainingCount, blocked, rng);

    return finalizeAnswerOptions([
        correctSymbolId,
        ...sameCategoryOptions,
        ...foilCategoryOptions,
        ...mixedOptions,
    ], correctSymbolId, optionCount, symbolVariant, rng);
};

const polarToPercent = (angleDegrees, radius) => {
    const radians = (angleDegrees * Math.PI) / 180;

    return {
        x: 50 + Math.cos(radians) * radius,
        y: 50 + Math.sin(radians) * radius,
    };
};

const normalizeAngleDegrees = (angleDegrees) => {
    const normalized = angleDegrees % 360;
    return normalized < 0 ? normalized + 360 : normalized;
};

const getAngleDeltaDegrees = (sourceAngle, targetAngle) => {
    const delta = Math.abs(normalizeAngleDegrees(sourceAngle) - normalizeAngleDegrees(targetAngle));
    return delta > 180 ? 360 - delta : delta;
};

const getPointAngleDegrees = (point) => (
    normalizeAngleDegrees(
        (Math.atan2((point?.y ?? 50) - 50, (point?.x ?? 50) - 50) * 180) / Math.PI
    )
);

const getVariantTrialScenario = (symbolVariant) => symbolVariant?.scenario?.trial || {};

const normalizeSafeZone = (safeZone, fallbackSafeZone) => {
    const fallback = fallbackSafeZone || {
        left: 0,
        right: 100,
        top: 0,
        bottom: 100,
    };

    return {
        left: clamp(readNumber(safeZone?.left, fallback.left), 0, 100),
        right: clamp(readNumber(safeZone?.right, fallback.right), 0, 100),
        top: clamp(readNumber(safeZone?.top, fallback.top), 0, 100),
        bottom: clamp(readNumber(safeZone?.bottom, fallback.bottom), 0, 100),
    };
};

const isPointInsideCenterExclusion = (x, y, centerExclusion) => {
    const exclusion = centerExclusion || {};
    const absDx = Math.max(0, readNumber(exclusion.absDx, 0));
    const absDy = Math.max(0, readNumber(exclusion.absDy, 0));
    const radius = Math.max(0, readNumber(exclusion.radius, 0));

    if (absDx > 0 && absDy > 0 && Math.abs(x - 50) < absDx && Math.abs(y - 50) < absDy) {
        return true;
    }

    return radius > 0 && Math.hypot(x - 50, y - 50) < radius;
};

const createRandomRectPoint = (safeZone, centerExclusion, rngInput, safetyLimit = 24) => {
    const rng = normalizeRng(rngInput);
    const fallbackSafeZone = {
        left: 18,
        right: 82,
        top: 18,
        bottom: 82,
    };
    const bounds = normalizeSafeZone(safeZone, fallbackSafeZone);
    let safety = 0;

    while (safety < safetyLimit) {
        safety += 1;
        const x = bounds.left + rng() * (bounds.right - bounds.left);
        const y = bounds.top + rng() * (bounds.bottom - bounds.top);

        if (!isPointInsideCenterExclusion(x, y, centerExclusion)) {
            return { x, y };
        }
    }

    return {
        x: bounds.left + ((bounds.right - bounds.left) / 2),
        y: bounds.top + ((bounds.bottom - bounds.top) / 2),
    };
};

const createSymbolMotion = (difficulty, rngInput) => {
    const rng = normalizeRng(rngInput);
    const moveX = -difficulty.motionPx + rng() * difficulty.motionPx * 2;
    const moveY = -difficulty.motionPx + rng() * difficulty.motionPx * 2;
    const spin = -difficulty.motionDeg + rng() * difficulty.motionDeg * 2;
    const durationJitter = -180 + rng() * 360;

    return {
        moveX: Math.round(moveX * 100) / 100,
        moveY: Math.round(moveY * 100) / 100,
        spin: Math.round(spin * 100) / 100,
        durationMs: Math.round(difficulty.motionDurationMs + durationJitter),
        delayMs: Math.round(-rng() * difficulty.motionDurationMs),
    };
};

const getGridSizeForDistractorCount = (distractorCount, gridSizeByDistractorCount) => {
    const rules = Array.isArray(gridSizeByDistractorCount)
        ? [...gridSizeByDistractorCount].sort((left, right) => (
            readNumber(right.minCount, 0) - readNumber(left.minCount, 0)
        ))
        : [];
    const matchingRule = rules.find((rule) => distractorCount >= readNumber(rule.minCount, 0));

    return Math.max(2, Math.round(readNumber(matchingRule?.size, 6)));
};

const buildRectDistractorCandidatePoints = (difficulty, fieldConfig, centerExclusion, rngInput) => {
    const rng = normalizeRng(rngInput);
    const safeZone = normalizeSafeZone(fieldConfig?.safeZone, {
        left: 10,
        right: 90,
        top: 10,
        bottom: 90,
    });
    const gridSize = getGridSizeForDistractorCount(
        difficulty.distractorCount,
        fieldConfig?.gridSizeByDistractorCount
    );
    const stepX = (safeZone.right - safeZone.left) / Math.max(1, gridSize - 1);
    const stepY = (safeZone.bottom - safeZone.top) / Math.max(1, gridSize - 1);
    const minPassCount = Math.max(1, Math.round(readNumber(fieldConfig?.minPassCount, 2)));
    const primaryJitterRatio = readNumber(fieldConfig?.primaryJitterRatio, 0.16);
    const secondaryJitterRatio = readNumber(fieldConfig?.secondaryJitterRatio, 0.12);
    const passCount = Math.max(
        minPassCount,
        Math.ceil(difficulty.distractorCount / Math.max(1, (gridSize * gridSize) - 6))
    );
    const candidates = [];

    for (let passIndex = 0; passIndex < passCount; passIndex += 1) {
        const phaseX = passIndex % 2 === 0 ? 0 : stepX / 2;
        const phaseY = passIndex % 2 === 0 ? 0 : stepY / 2;
        const columnCount = gridSize - (phaseX > 0 ? 1 : 0);
        const rowCount = gridSize - (phaseY > 0 ? 1 : 0);
        const jitterRatio = passIndex === 0 ? primaryJitterRatio : secondaryJitterRatio;
        const jitterX = stepX * jitterRatio;
        const jitterY = stepY * jitterRatio;

        for (let rowIndex = 0; rowIndex < rowCount; rowIndex += 1) {
            for (let columnIndex = 0; columnIndex < columnCount; columnIndex += 1) {
                const xBase = safeZone.left + phaseX + (columnIndex * stepX);
                const yBase = safeZone.top + phaseY + (rowIndex * stepY);
                const x = clamp(xBase + ((rng() * 2) - 1) * jitterX, safeZone.left, safeZone.right);
                const y = clamp(yBase + ((rng() * 2) - 1) * jitterY, safeZone.top, safeZone.bottom);

                if (isPointInsideCenterExclusion(x, y, centerExclusion)) {
                    continue;
                }

                candidates.push({ x, y });
            }
        }
    }

    return shuffle(candidates, rng);
};

const buildAlignedGridDistractorCandidatePoints = (fieldConfig, centerExclusion, rngInput) => {
    const rng = normalizeRng(rngInput);
    const explicitPoints = Array.isArray(fieldConfig?.points)
        ? fieldConfig.points
            .map((point) => ({
                x: clamp(readNumber(point?.x, 50), 0, 100),
                y: clamp(readNumber(point?.y, 50), 0, 100),
            }))
            .filter((point) => !isPointInsideCenterExclusion(point.x, point.y, centerExclusion))
        : [];

    if (explicitPoints.length) {
        return fieldConfig?.shuffle === false ? explicitPoints : shuffle(explicitPoints, rng);
    }

    const safeZone = normalizeSafeZone(fieldConfig?.safeZone, {
        left: 10,
        right: 90,
        top: 10,
        bottom: 90,
    });
    const columns = Math.max(1, Math.round(readNumber(fieldConfig?.columns, 5)));
    const rows = Math.max(1, Math.round(readNumber(fieldConfig?.rows, 4)));
    const candidates = [];

    for (let rowIndex = 0; rowIndex < rows; rowIndex += 1) {
        for (let columnIndex = 0; columnIndex < columns; columnIndex += 1) {
            const x = safeZone.left + ((columnIndex + 0.5) / columns) * (safeZone.right - safeZone.left);
            const y = safeZone.top + ((rowIndex + 0.5) / rows) * (safeZone.bottom - safeZone.top);

            if (!isPointInsideCenterExclusion(x, y, centerExclusion)) {
                candidates.push({ x, y });
            }
        }
    }

    return fieldConfig?.shuffle === false ? candidates : shuffle(candidates, rng);
};

const buildPolarDistractorCandidatePoints = (difficulty, fieldConfig, rngInput) => {
    const rng = normalizeRng(rngInput);
    const sectorCount = Math.max(4, Math.round(readNumber(fieldConfig?.sectorCount, 16)));
    const sectorStep = 360 / sectorCount;
    const minRadius = readNumber(fieldConfig?.minRadius, 22);
    const maxRadius = readNumber(fieldConfig?.maxRadius, 47);
    const phaseOffsetFactor = readNumber(fieldConfig?.phaseOffsetFactor, 0.33);
    const phaseJitterRatio = readNumber(fieldConfig?.phaseJitterRatio, 0.24);
    const angleJitterRatio = readNumber(fieldConfig?.angleJitterRatio, 0.34);
    const radiusJitter = readNumber(fieldConfig?.radiusJitter, 2.6);
    const ringCount = Math.max(2, Math.ceil(difficulty.distractorCount / sectorCount) + 1);
    const sectorOrder = shuffle(
        Array.from({ length: sectorCount }, (_, index) => index),
        rng
    );
    const candidates = [];

    for (let ringIndex = 0; ringIndex < ringCount; ringIndex += 1) {
        const ringRatio = ringCount <= 1 ? 0.5 : ringIndex / (ringCount - 1);
        const radiusBase = minRadius + ringRatio * (maxRadius - minRadius);
        const phaseOffset = (ringIndex * sectorStep * phaseOffsetFactor)
            + ((-phaseJitterRatio / 2) + (rng() * phaseJitterRatio)) * sectorStep;

        sectorOrder.forEach((_, orderIndex) => {
            const slot = sectorOrder[(orderIndex + ringIndex) % sectorOrder.length];
            const angle = (slot * sectorStep)
                + phaseOffset
                + ((-angleJitterRatio / 2) + (rng() * angleJitterRatio)) * sectorStep;
            const radius = clamp(
                radiusBase + (-1 + rng() * 2) * radiusJitter,
                minRadius,
                maxRadius
            );

            candidates.push(polarToPercent(angle, radius));
        });
    }

    return candidates;
};

const buildTwoCircleDistractorCandidatePoints = (fieldConfig) => {
    const slotCount = Math.max(4, Math.round(readNumber(fieldConfig?.slotCount, 16)));
    const innerRadius = readNumber(fieldConfig?.innerRadius, 25);
    const outerRadius = readNumber(fieldConfig?.outerRadius, 42);
    const angleOffset = readNumber(fieldConfig?.angleOffset, -65);
    const innerCount = Math.ceil(slotCount / 2);
    const outerCount = Math.max(1, slotCount - innerCount);
    const toPoint = (index, total, radius, offset = 0) => {
        const angle = ((360 / Math.max(1, total)) * index) + angleOffset + offset;

        return polarToPercent(angle, radius);
    };
    const points = [];

    for (let index = 0; index < innerCount; index += 1) {
        points.push({
            ...toPoint(index, innerCount, innerRadius),
            ring: 'inner',
            ringIndex: index,
        });
    }

    for (let index = 0; index < outerCount; index += 1) {
        points.push({
            ...toPoint(index, outerCount, outerRadius, 360 / Math.max(1, outerCount * 2)),
            ring: 'outer',
            ringIndex: index,
        });
    }

    return points;
};

const buildDistractorCandidatePoints = (difficulty, symbolVariant, rngInput) => {
    const trialScenario = getVariantTrialScenario(symbolVariant);
    const distractorField = trialScenario.distractorField || {};

    if (distractorField.kind === 'two-circles') {
        return buildTwoCircleDistractorCandidatePoints(distractorField.twoCircles);
    }

    if (distractorField.kind === 'rect-grid') {
        return buildRectDistractorCandidatePoints(
            difficulty,
            distractorField.rect,
            trialScenario.centerExclusion,
            rngInput
        );
    }

    if (distractorField.kind === 'aligned-grid') {
        return buildAlignedGridDistractorCandidatePoints(
            distractorField.grid,
            trialScenario.centerExclusion,
            rngInput
        );
    }

    return buildPolarDistractorCandidatePoints(difficulty, distractorField.polar, rngInput);
};

const selectPeripheralReplacementPoint = (candidatePoints, peripheralPosition, difficulty, symbolVariant) => {
    const trialScenario = getVariantTrialScenario(symbolVariant);
    const peripheralPlacement = trialScenario.peripheralPlacement || {};
    const targetAngle = peripheralPosition?.angle ?? 0;
    const idealRadius = difficulty.targetRadius;

    if (peripheralPlacement.kind !== 'reuse-candidate') {
        return polarToPercent(targetAngle, idealRadius);
    }

    const maxAngleDelta = readNumber(peripheralPlacement.maxAngleDelta, 18);
    const candidatePool = peripheralPlacement.ring
        ? candidatePoints.filter((point) => point.ring === peripheralPlacement.ring)
        : candidatePoints;
    const scoredCandidates = (candidatePool.length ? candidatePool : candidatePoints).map((point) => {
        const angle = getPointAngleDegrees(point);
        const angleDelta = getAngleDeltaDegrees(angle, targetAngle);
        const radius = Math.hypot(point.x - 50, point.y - 50);
        const radiusDelta = Math.abs(radius - idealRadius);

        return {
            point,
            angleDelta,
            radiusDelta,
            score: angleDelta * 10 + radiusDelta,
        };
    });
    const matchingCandidates = scoredCandidates.filter(
        (candidate) => candidate.angleDelta <= maxAngleDelta
    );
    const pool = matchingCandidates.length ? matchingCandidates : scoredCandidates;

    if (!pool.length) {
        return polarToPercent(targetAngle, idealRadius);
    }

    return pool
        .slice()
        .sort((left, right) => left.score - right.score)[0]
        .point;
};

const relaxSpacingValue = (spacingRule, relaxRatio) => {
    const start = readNumber(spacingRule?.start, 0);
    const min = readNumber(spacingRule?.min, start);
    const relax = readNumber(spacingRule?.relax, 0);

    return clamp(start - (relaxRatio * relax), min, start);
};

const isDistractorPlacementBlocked = (
    point,
    items,
    targetPoint,
    symbolVariant,
    relaxRatio = 0,
) => {
    const spacing = getVariantTrialScenario(symbolVariant).spacing || {};
    const targetClearance = relaxSpacingValue(spacing.targetClearance, relaxRatio);
    const minDistance = relaxSpacingValue(spacing.minDistance, relaxRatio);
    const laneShortGap = relaxSpacingValue(spacing.laneShortGap, relaxRatio);
    const laneLongGap = relaxSpacingValue(spacing.laneLongGap, relaxRatio);

    if (Math.hypot(point.x - targetPoint.x, point.y - targetPoint.y) < targetClearance) {
        return true;
    }

    return items.some((item) => {
        const dx = Math.abs(point.x - item.x);
        const dy = Math.abs(point.y - item.y);

        if (Math.hypot(dx, dy) < minDistance) {
            return true;
        }

        if (dx < laneShortGap && dy < laneLongGap) {
            return true;
        }

        if (dy < laneShortGap && dx < laneLongGap) {
            return true;
        }

        return false;
    });
};

const createFallbackCandidatePoint = (symbolVariant, rngInput) => {
    const rng = normalizeRng(rngInput);
    const trialScenario = getVariantTrialScenario(symbolVariant);
    const fallbackField = trialScenario.fallbackField || {};

    if (fallbackField.kind === 'rect') {
        return createRandomRectPoint(
            fallbackField.safeZone,
            trialScenario.centerExclusion,
            rng,
            Math.round(readNumber(fallbackField.safetyLimit, 24))
        );
    }

    const minRadius = readNumber(fallbackField.minRadius, 22);
    const maxRadius = readNumber(fallbackField.maxRadius, 47);

    return polarToPercent(rng() * 360, minRadius + (rng() * (maxRadius - minRadius)));
};

const createDistractors = (
    difficulty,
    targetPosition,
    noiseSymbolId,
    symbolVariant,
    rngInput,
    options = {},
) => {
    const rng = normalizeRng(rngInput);
    const trialScenario = getVariantTrialScenario(symbolVariant);
    const spacing = trialScenario.spacing || {};
    const noise = trialScenario.noise || {};
    const targetPoint = options.targetPoint || polarToPercent(targetPosition.angle, difficulty.targetRadius);
    const reservedPoint = options.reservedPoint || null;
    const reservedClearance = readNumber(spacing.reservedClearance, 0);
    const candidatePoints = Array.isArray(options.candidatePoints)
        ? [...options.candidatePoints]
        : buildDistractorCandidatePoints(difficulty, symbolVariant, rng);
    const items = [];
    const safetyLimit = candidatePoints.length + difficulty.distractorCount * 10;
    let safety = 0;
    let candidateIndex = 0;

    while (items.length < difficulty.distractorCount && safety < safetyLimit) {
        safety += 1;
        const point = candidateIndex < candidatePoints.length
            ? candidatePoints[candidateIndex++]
            : createFallbackCandidatePoint(symbolVariant, rng);
        const relaxRatio = safetyLimit <= 1 ? 1 : safety / safetyLimit;

        if (reservedPoint && Math.hypot(point.x - reservedPoint.x, point.y - reservedPoint.y) < reservedClearance) {
            continue;
        }

        if (isDistractorPlacementBlocked(point, items, targetPoint, symbolVariant, relaxRatio)) {
            continue;
        }

        items.push({
            id: `noise-${items.length}`,
            symbol: noiseSymbolId,
            x: point.x,
            y: point.y,
            size: readNumber(noise.baseSize, 16) + (
                noise.allowSizeVariance === true
                    ? rng() * readNumber(noise.sizeVariance, 7)
                    : 0
            ),
            rotation: noise.allowRotation === true ? Math.round(-18 + rng() * 36) : 0,
            opacity: clamp(
                difficulty.noiseOpacity + readNumber(noise.opacityBoost, 0),
                readNumber(noise.minOpacity, 0),
                readNumber(noise.maxOpacity, 1)
            ),
            motion: noise.lockMotion === true ? {
                moveX: 0,
                moveY: 0,
                spin: 0,
                durationMs: difficulty.motionDurationMs,
                delayMs: 0,
            } : createSymbolMotion(difficulty, rng),
        });
    }

    return items;
};

const createMaskDots = (rngInput) => {
    const rng = normalizeRng(rngInput);

    return Array.from({ length: 320 }, (_, index) => ({
        id: `mask-${index}`,
        x: rng() * 100,
        y: rng() * 100,
        size: 1 + rng() * 3.8,
        opacity: 0.24 + rng() * 0.66,
        light: rng() > 0.48,
    }));
};

const createTrial = (level, modeId = MODE_IDS.CLASSIC, optionsOrRng) => {
    const {
        rngInput,
        optionLimit,
        symbolVariant: requestedFamilyId,
        scenarioId,
        allowedSymbolIds,
    } = normalizeTrialOptions(optionsOrRng);
    const rng = normalizeRng(rngInput);
    const requestedVariant = getUFOVSymbolVariant(requestedFamilyId);
    const isDirectPackVariant = requestedFamilyId !== FRUIT_SYMBOL_VARIANT_ID;
    const scenarioFamilyId = FRUIT_SYMBOL_VARIANT_ID;
    const effectiveScenarioId = scenarioId || (isDirectPackVariant ? requestedVariant.id : '');
    const shouldRandomizeScenario = !effectiveScenarioId && scenarioFamilyId === FRUIT_SYMBOL_VARIANT_ID;
    const scenarioContext = getUFOVScenarioRuntimeContext(scenarioFamilyId, level, {
        scenarioId: effectiveScenarioId,
        pickMode: shouldRandomizeScenario ? 'random' : 'rotation',
        rng,
    });
    const baseSymbolVariant = isDirectPackVariant
        ? requestedVariant
        : getUFOVSymbolVariant(scenarioContext.activeVariantId);
    const symbolVariant = buildFilteredSymbolVariant(baseSymbolVariant, allowedSymbolIds);
    const baseDifficulty = getDifficulty(level, modeId);
    const trialScenario = getVariantTrialScenario(symbolVariant);
    const maxDistractorCount = Number(trialScenario.maxDistractorCount);
    const normalizedOptionLimit = Number.isFinite(optionLimit)
        ? clamp(
            Math.floor(optionLimit),
            4,
            Math.min(
                MAX_ANSWER_OPTION_COUNT,
                symbolVariant.maxOptionCount || symbolVariant.pool.length
            )
        )
        : null;
    const difficulty = {
        ...baseDifficulty,
        optionCount: Math.min(
            baseDifficulty.optionCount,
            MAX_ANSWER_OPTION_COUNT,
            symbolVariant.maxOptionCount || symbolVariant.pool.length,
            normalizedOptionLimit || Number.POSITIVE_INFINITY
        ),
        distractorCount: Number.isFinite(maxDistractorCount)
            ? Math.min(baseDifficulty.distractorCount, Math.max(0, Math.floor(maxDistractorCount)))
            : baseDifficulty.distractorCount,
    };
    const centerCategory = pickCategory(symbolVariant, rng);
    const peripheralCategory = pickCategory(symbolVariant, rng, centerCategory.id);
    const centerSymbol = pickSymbolFromCategory(symbolVariant, centerCategory.id, [], rng);
    const noiseSymbol = pickSymbolOutsideCategory(symbolVariant, peripheralCategory.id, [centerSymbol], rng);
    const noiseCategoryId = symbolVariant.categoryBySymbol.get(noiseSymbol) || '';
    const peripheralSymbol = pickSymbolFromCategory(symbolVariant, peripheralCategory.id, [centerSymbol, noiseSymbol], rng);
    const peripheralPosition = pick(POSITIONS, rng);
    const candidatePoints = buildDistractorCandidatePoints(difficulty, symbolVariant, rng);
    const reservePeripheralSlot = getVariantTrialScenario(symbolVariant).peripheralPlacement?.reserveDistractorSlot === true;
    const peripheralPoint = selectPeripheralReplacementPoint(
        candidatePoints,
        peripheralPosition,
        difficulty,
        symbolVariant
    );
    const distractors = createDistractors(
        difficulty,
        peripheralPosition,
        noiseSymbol,
        symbolVariant,
        rng,
        {
            candidatePoints,
            reservedPoint: reservePeripheralSlot ? peripheralPoint : null,
            targetPoint: peripheralPoint,
        }
    );

    return {
        id: `${normalizeSeed(level)}-${Math.round(rng() * 1e9).toString(16)}`,
        difficulty,
        requestedScenarioId: effectiveScenarioId || scenarioContext.requestedScenario?.id || '',
        requestedScenarioLabel: isDirectPackVariant
            ? requestedVariant.label
            : scenarioContext.requestedScenario?.label || '',
        activeScenarioId: isDirectPackVariant
            ? effectiveScenarioId || requestedVariant.id
            : scenarioContext.activeScenario?.id || '',
        activeScenarioLabel: isDirectPackVariant
            ? requestedVariant.label
            : scenarioContext.activeScenario?.label || '',
        symbolVariant: symbolVariant.id,
        centerSymbol,
        centerCategoryId: centerCategory.id,
        noiseSymbol,
        noiseCategoryId,
        peripheralSymbol,
        peripheralCategoryId: peripheralCategory.id,
        peripheralPosition,
        peripheralPoint,
        peripheralMotion: createSymbolMotion(difficulty, rng),
        centerOptions: createAnswerOptions(centerSymbol, difficulty, symbolVariant, rng),
        peripheralOptions: createAnswerOptions(peripheralSymbol, difficulty, symbolVariant, rng),
        distractors,
        maskDots: createMaskDots(rng),
    };
};

const getVideoBucketByLabel = (label) => (
    VIDEO_BUCKETS.find((bucket) => bucket.label.toLowerCase() === `${label}`.toLowerCase()) || null
);

const getVideoBucketForLevel = (level) => (
    VIDEO_BUCKETS.find((bucket) => level >= bucket.minLevel && level <= bucket.maxLevel) || VIDEO_BUCKETS[0]
);

const interpolateInt = (start, end, ratio) => Math.round(start + ((end - start) * ratio));

const buildVideoDifficultyProfile = (level) => {
    const safeLevel = clamp(Number(level) || 0, LEVEL_MIN, LEVEL_MAX);
    const bucket = getVideoBucketForLevel(safeLevel);
    const ratio = bucket.maxLevel === bucket.minLevel
        ? 0
        : (safeLevel - bucket.minLevel) / (bucket.maxLevel - bucket.minLevel);
    const ageBadge = interpolateInt(bucket.minAge, bucket.maxAge, ratio);

    return {
        bucket: bucket.label,
        label: bucket.label,
        difficultyLabel: bucket.label,
        ageBadge,
        ageLabel: `${ageBadge} years old`,
        level: safeLevel,
    };
};

const normalizeStringList = (items) => {
    if (!Array.isArray(items)) {
        return [];
    }

    return [...new Set(items.map((item) => `${item || ''}`.trim()).filter(Boolean))];
};

const pickVideoBucket = (rng, requestedBucketLabel, bucketLabels = []) => {
    const requestedBucket = getVideoBucketByLabel(requestedBucketLabel);

    if (requestedBucket) {
        return requestedBucket;
    }

    const bucketPool = normalizeStringList(bucketLabels)
        .map(getVideoBucketByLabel)
        .filter(Boolean);

    return pick(bucketPool.length ? bucketPool : VIDEO_BUCKETS, rng);
};

const pickVideoScenarioId = (rng, requestedScenarioId, scenarioIds = []) => {
    const fixedScenarioId = normalizeScenarioId(requestedScenarioId);

    if (fixedScenarioId) {
        return fixedScenarioId;
    }

    const liveScenarioIds = new Set(
        getUFOVLiveScenarioCatalog(FRUIT_SYMBOL_VARIANT_ID).map((scenario) => scenario.id)
    );
    const scenarioPool = normalizeStringList(scenarioIds)
        .map(normalizeScenarioId)
        .filter((scenarioId) => liveScenarioIds.has(scenarioId));

    return scenarioPool.length ? pick(scenarioPool, rng) : '';
};

const normalizeSceneDurations = (difficulty, sceneDurations = {}) => {
    const readDuration = (key, fallback, min = 80, max = 10000) => (
        clamp(Math.round(readNumber(sceneDurations[key], fallback)), min, max)
    );

    return {
        countdownMs: readDuration('countdownMs', 3000, 250),
        preStimulusBlankMs: readDuration('preStimulusBlankMs', 1000, 0),
        stimulusMs: readDuration('stimulusMs', difficulty.exposureMs, 80),
        postStimulusBlankMs: readDuration('postStimulusBlankMs', 1000, 0),
        centerQuestionMs: readDuration('centerQuestionMs', 2500, 250),
        directionQuestionMs: readDuration('directionQuestionMs', 2500, 250),
        resultMs: readDuration('resultMs', 2500, 250),
    };
};

const pickVideoTheme = (rngInput, requestedThemeId, themeIds = []) => {
    const rng = normalizeRng(rngInput);

    if (requestedThemeId) {
        return VIDEO_THEMES.find((theme) => theme.id === requestedThemeId) || VIDEO_THEMES[0];
    }

    const themePool = normalizeStringList(themeIds)
        .map((themeId) => VIDEO_THEMES.find((theme) => theme.id === themeId))
        .filter(Boolean);

    return pick(themePool.length ? themePool : VIDEO_THEMES, rng);
};

const buildVideoSpec = (seed, options = {}) => {
    const normalizedSeed = normalizeSeed(seed || createSeed());
    const rng = createSeededRng(normalizedSeed);
    const requestedSymbolVariant = normalizeSymbolVariantId(
        options.symbolVariant || options.variant || FRUIT_SYMBOL_VARIANT_ID
    );
    const hasExplicitLevel = options.level !== undefined && options.level !== null && options.level !== '';
    const randomBucket = pickVideoBucket(rng, options.bucket, options.buckets || options.bucketOptions);
    const level = hasExplicitLevel
        ? clamp(Number(options.level) || 0, LEVEL_MIN, LEVEL_MAX)
        : randomInt(rng, randomBucket.minLevel, randomBucket.maxLevel);
    const difficultyProfile = buildVideoDifficultyProfile(level);
    const scenarioId = pickVideoScenarioId(rng, options.scenarioId, options.scenarioIds || options.scenarioOptions);
    const trial = createTrial(level, MODE_IDS.CLASSIC, {
        optionLimit: VIDEO_OPTION_LIMIT,
        rng,
        symbolVariant: requestedSymbolVariant,
        scenarioId,
    });
    const difficulty = trial.difficulty;
    const sceneDurations = normalizeSceneDurations(difficulty, options.sceneDurations || {});
    const theme = pickVideoTheme(rng, options.themeId, options.themeIds || options.themeOptions);
    const progress = {
        level,
        bestLevel: level,
        lastLevelDelta: 0,
        trials: 1,
        perfectTrials: 1,
        centerHits: 1,
        positionHits: 1,
        peripheralHits: 0,
    };
    const feedback = {
        centerCorrect: true,
        positionCorrect: true,
        peripheralCorrect: true,
        perfect: true,
        levelDelta: 0,
        difficultyChanged: false,
        previousLevel: level,
        modeId: MODE_IDS.CLASSIC,
    };

    return {
        specVersion: 1,
        seed: normalizedSeed,
        modeId: MODE_IDS.CLASSIC,
        symbolVariant: trial.symbolVariant,
        requestedSymbolVariant,
        requestedScenarioId: trial.requestedScenarioId,
        activeScenarioId: trial.activeScenarioId,
        level,
        bucket: difficultyProfile.bucket,
        difficultyLabel: difficultyProfile.difficultyLabel,
        ageBadge: difficultyProfile.ageBadge,
        ageLabel: difficultyProfile.ageLabel,
        difficulty,
        theme,
        trial,
        questions: {
            centerStep: '1 / 2',
            directionStep: '2 / 2',
        },
        sceneDurations: {
            ...sceneDurations,
        },
        render: {
            width: 1080,
            height: 1920,
            fps: 60,
            countdownFrameCount: 60,
            format: 'mp4',
            silent: true,
            scenes: VIDEO_SCENE_IDS,
        },
        result: {
            progress,
            feedback,
        },
    };
};

const ufovTrials = {
    MODE_IDS,
    POSITIONS,
    VIDEO_BUCKETS,
    VIDEO_OPTION_LIMIT,
    VIDEO_SCENE_IDS,
    VIDEO_THEMES,
    buildVideoDifficultyProfile,
    buildVideoSpec,
    createSeed,
    createSeededRng,
    createTrial,
    getDifficulty,
    getVideoBucketByLabel,
    getVideoBucketForLevel,
    pickVideoTheme,
};

module.exports = ufovTrials;
module.exports.default = ufovTrials;
